import { useQuery } from '@tanstack/react-query';
import type { Period } from '@nav/shared';

import {
  getFundSummary,
  getFunds,
  getHoldings,
  getInsights,
  getMarketSnapshot,
  getNavDaily,
  getNavMonthly,
  type NavWindow,
} from '@/lib/api';

/**
 * Query keys, in one place, so a cache invalidation cannot miss a caller.
 * Shape: ['funds'] / ['fund', code, …] — prefix-invalidating a fund clears
 * everything derived from it.
 */
export const fundKeys = {
  all: ['funds'] as const,
  list: () => [...fundKeys.all, 'list'] as const,
  summary: (code: string) => [...fundKeys.all, code, 'summary'] as const,
  navMonthly: (code: string, window: NavWindow) =>
    [...fundKeys.all, code, 'nav-monthly', window] as const,
  navDaily: (code: string, limit: number) => [...fundKeys.all, code, 'nav-daily', limit] as const,
  holdings: (code: string, period: Period | null) =>
    [...fundKeys.all, code, 'holdings', period ?? 'latest'] as const,
  insights: (code: string) => [...fundKeys.all, code, 'insights'] as const,
};

export function useFundsQuery() {
  return useQuery({
    queryKey: fundKeys.list(),
    queryFn: getFunds,
    // The fund list changes when an analyst uploads, i.e. approximately never
    // during a session. Hold it longer than the global default.
    staleTime: 5 * 60_000,
  });
}

export function useFundSummaryQuery(code: string | null) {
  return useQuery({
    queryKey: fundKeys.summary(code ?? ''),
    queryFn: () => getFundSummary(code!),
    enabled: Boolean(code),
  });
}

/**
 * The windowed series behind the NAV card and the monthly table.
 * The `window` goes to the server (per `NavMonthlyQuery` in the contract) rather
 * than being sliced in the browser, so the pills and the API agree on what "1Y"
 * means even when months are missing from the middle of the series.
 */
export function useNavMonthlyQuery(code: string | null, window: NavWindow) {
  return useQuery({
    queryKey: fundKeys.navMonthly(code ?? '', window),
    queryFn: () => getNavMonthly(code!, { window }),
    enabled: Boolean(code),
  });
}

/**
 * The full series. The year-comparison table and the YoY chart group by calendar
 * year, so they must never see a truncated window — otherwise switching the
 * timeframe pill would silently change which years exist.
 */
export function useNavMonthlyAllQuery(code: string | null) {
  return useNavMonthlyQuery(code, 'all');
}

export function useNavDailyQuery(code: string | null, limit = 30) {
  return useQuery({
    queryKey: fundKeys.navDaily(code ?? '', limit),
    queryFn: () => getNavDaily(code!, { limit }),
    enabled: Boolean(code),
  });
}

export function useHoldingsQuery(code: string | null, period: Period | null) {
  return useQuery({
    queryKey: fundKeys.holdings(code ?? '', period),
    queryFn: () => getHoldings(code!, period ?? undefined),
    enabled: Boolean(code),
  });
}

/**
 * Live USD/IDR + (if configured) US macro figures — free providers, cached
 * 6h server-side. Fails soft: a snapshot with every field `null` is a normal,
 * successful result, not a query error, so the macro strip always has
 * something to render (its own editorial fallback).
 */
export function useMarketSnapshotQuery() {
  return useQuery({
    queryKey: ['market', 'snapshot'] as const,
    queryFn: getMarketSnapshot,
    staleTime: 60 * 60_000,
    retry: 1,
  });
}

/**
 * AI-written, cited, analyst-approved insight cards.
 *
 * Fails soft in the same way the market snapshot does: an empty list is a normal
 * successful result (feature off, nothing generated, nothing approved yet), and
 * the two computed cards from `insightsEngine` render regardless. This query
 * must never be able to blank the "Insight & Analisa" section.
 *
 * Published cards change at most weekly, so the cache is held for an hour.
 */
export function useInsightsQuery(code: string | null) {
  return useQuery({
    queryKey: fundKeys.insights(code ?? ''),
    queryFn: () => getInsights(code!),
    enabled: Boolean(code),
    staleTime: 60 * 60_000,
    retry: 1,
  });
}
